// EEN FEIT, EEN BRON: in welke fase een proces STAAT.
//
// Er waren twee antwoorden op die vraag. `deal.status` wordt afgeleid uit de
// signalen (IV-datums, het aanbiedingsvinkje) en loopt dus vanzelf mee, terwijl
// `deal.phaseChecklist.phaseIndex` alleen verschuift wanneer iemand op 'Fase
// afronden' klikt. Op 21 september 2026 stonden 111 processen op status IV1 of
// hoger met fase-index 0: de kop van het procesvenster zei IV2 en de fasekaart
// eronder zei Kwalificeren, en er ging niets stuk. Dat is precies de toets.
//
// De regel: de fase is een ONDERGRENS-spel. De opgeslagen index blijft wat hij
// is (dat is een handeling van een consultant, geen afleiding), maar de status
// duwt er een ondergrens onder. De EFFECTIEVE fase is de hoogste van die twee,
// en de fasen die daartussen zijn overgeslagen dragen dat als eigenschap.
//
// Elke lezer van "waar staat dit proces" leest effectivePhase. Wie de OPGESLAGEN
// index nodig heeft (de statusafleiding zelf, de schrijvers, de poort naar
// pre-close) leest hem bewust, en staat als uitzondering met een telling in
// lib/one-fact-one-source.test.mjs.
//
// De koppeling fase -> status hangt aan de fase zelf, als `fromStatus`: een
// director bewerkt de fasen in Beheer en zet er dus ook bij vanaf welke status
// een fase begint. ensurePhaseFloors vult hem op elk leespad aan, net als
// ensureRequiredActions, zodat een oudere opgeslagen fasenreeks er niet zonder
// valt.

import { IV_RANK } from './scorecard-gate.mjs';
import { derivePlacementStatus } from './placement-status.mjs';

// De statussen die een fase-ondergrens kunnen zijn, in de volgorde van de
// ladder. Beheer toont deze lijst in het veld 'Start bij status'.
export const FROM_STATUS_OPTIONS = ['IV1', 'IV2', 'IV3', 'IV4', 'IV5', 'IV6', 'Aanbieding arbeidscontract'];

// Standaard-ondergrens op basis van de naam van de fase. Dekt de runtime-reeks
// (Kwalificeren, Introduceren, Voorbereiding IV1, Voorbereiding vervolgronde,
// Pre-Closen/Close) en de statische terugval (Intake, Search, Interviewronden,
// Closing). Geen treffer is een lege string: die fase kent geen ondergrens.
function defaultFromStatus(name) {
  const s = String(name || '');
  if (/IV\s*1|interviewronde/i.test(s)) return 'IV1';
  if (/IV\s*2|vervolgronde/i.test(s)) return 'IV2';
  if (/clos/i.test(s)) return 'Aanbieding arbeidscontract';
  return '';
}

// Vult `fromStatus` aan op elke placement-fase die hem mist. Muteert en
// retourneert de blob, dezelfde vorm als ensureRequiredActions, zodat elk
// leespad hem er in een adem doorheen kan halen. Een BESTAANDE waarde blijft
// staan, ook een expliciete lege string: dat is de keuze van een director en
// geen ontbrekende sleutel.
export function ensurePhaseFloors(data) {
  if (!data || typeof data !== 'object') return data;
  const phases = data.placement;
  if (!Array.isArray(phases)) return data;
  for (const p of phases) {
    if (!p || typeof p !== 'object') continue;
    if ('fromStatus' in p) continue;
    p.fromStatus = defaultFromStatus(p.phase);
  }
  return data;
}

// De rang van een status op de IV-ladder. EEN ladder: IV_RANK uit
// lib/scorecard-gate.mjs, dezelfde die de scorecard-poort gebruikt.
const rankOf = (status) => IV_RANK[String(status || '')] || 0;

// De status waar de ondergrens op leunt. Een opgeslagen status wint; ontbreekt
// die, dan leest derivePlacementStatus hem uit de signalen. lastPhaseIndex is
// bewust NaN: de fase-index mag hier NOOIT meewegen, anders duwt de fase de
// status omhoog die vervolgens de fase weer omhoog duwt.
export function statusOf(deal) {
  return deal?.status || derivePlacementStatus(deal, NaN);
}

// De hoogste fase-index waarvan de ondergrens al bereikt is. Alleen placement:
// BD en sourcing kennen deze koppeling niet en houden 0.
export function phaseFloor(deal, phases) {
  if (!deal || deal.track !== 'placement') return 0;
  const list = Array.isArray(phases) ? phases : [];
  if (!list.length) return 0;
  const rank = rankOf(statusOf(deal));
  if (!rank) return 0;
  let floor = 0;
  for (let i = 0; i < list.length; i++) {
    const r = rankOf(list[i] && list[i].fromStatus);
    if (r && r <= rank && i > floor) floor = i;
  }
  return Math.min(floor, list.length - 1);
}

// De opgeslagen index, geklemd op de reeks. De ENIGE plek buiten de bewuste
// uitzonderingen die phaseChecklist.phaseIndex rechtstreeks leest.
export function storedPhaseIndex(deal, phases) {
  const last = Math.max(0, (Array.isArray(phases) ? phases.length : 1) - 1);
  const raw = Number(deal && deal.phaseChecklist && deal.phaseChecklist.phaseIndex);
  if (!Number.isFinite(raw) || raw <= 0) return 0;
  return Math.min(last, Math.floor(raw));
}

// { stored, floor, index, skipped }. `index` is de fase die elk scherm toont,
// `skipped` zijn de indexen die het proces heeft overgeslagen doordat de status
// er overheen liep (stored t/m index - 1). Een overgeslagen fase is GEEN
// vergeten fase (eigenaarsbesluit): hij telt niet mee in de gemiste stappen.
export function effectivePhase(deal, phases) {
  const list = Array.isArray(phases) ? phases : [];
  const last = Math.max(0, list.length - 1);
  const stored = storedPhaseIndex(deal, list);
  const floor = phaseFloor(deal, list);
  const index = Math.min(last, Math.max(stored, floor));
  const skipped = [];
  for (let i = stored; i < index; i++) skipped.push(i);
  return { stored, floor, index, skipped };
}
